import { useLayoutContext } from '@/container/context/layout/layout-context';
import { useProfile } from '@components/profile-dropdown/hooks/use-profile-dropdown';
import { SidebarMembersProps } from '@modules/members/members.types';
import { sortMembers } from '@modules/members/utils/sort-members';
import { useScreenSize } from '@shared/hooks/use-screen';
import { Empty, Menu, Skeleton } from 'antd';
import { MenuItemType } from 'antd/es/menu/interface';
import { MenuProps } from 'antd/lib';
import clsx from 'clsx';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { RiArrowDownSLine } from 'react-icons/ri';
import { useNavigate, useParams } from 'react-router-dom';
import { AddMember } from './add-member';
import { MemberCard } from './member-card';
import { MobileMembersView } from './mobile-sidebar-members.view';
import { useInviteMember } from '@modules/settings/subscription/hooks/use-invite-member';
import { InviteMemberModal } from '@modules/settings/subscription/components/modal-invite-member';
import { FaChevronRight } from 'react-icons/fa6';

const className: MenuItemType['className'] = 'group pl-1.5! p-0! mx-0! h-10! lg:h-11! w-full! overflow-visible!';

function SidebarMembers(props: SidebarMembersProps) {
  const { isMobile } = useScreenSize();
  const { memberId = '' } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    workspaceMembers: { data, initialLoading, hasNextPage },
    layoutContext,
  } = props;
  const { closeSidebarMobileDrawer, sidebarCollapsed } = layoutContext;
  const { isSubmenuOverlay, setIsSubmenuOverlay } = useLayoutContext();
  const members = data?.data ? [...data.data] : [];
  const { loggedInUserId } = useProfile();
  const inviteController = useInviteMember();
  const filteredMembers = sortMembers(members, loggedInUserId);
  const items: MenuProps['items'] = [
    {
      key: 'members',
      label: (
        <div className="text-text-600 flex justify-between items-center ml-0.5">
          <span className="uppercase leading-4! text-xs tracking-wider">{t('member', { count: 5 })}</span>
          <AddMember />
        </div>
      ),
      expandIcon: ({ isOpen }) => (
        <RiArrowDownSLine size={20} className={`transition-transform ${isOpen ? 'rotate-0' : 'rotate-180'}`} />
      ),
      className: clsx(
        'mx-auto! [&_.ant-menu-submenu-title]:hover:bg-gray-200! [&_.ant-menu-submenu-title]:w-full!',
        '[&_.ant-menu-submenu-title]:my-0! [&_.ant-menu-submenu-title]:mx-0! [&_.ant-menu-submenu-title]:px-0.5!'
      ),
      children: filteredMembers
        ?.map((member) => ({
          key: member?.id + '',
          isSelf: loggedInUserId === member?.user?.id,
          label: <MemberCard {...member} />,
          onClick: () => {
            closeSidebarMobileDrawer();
            if (loggedInUserId === member?.user?.id) return;
            navigate({
              pathname: '/conversations/member/' + member?.id,
            });
          },
        }))
        .map(({ isSelf, ...member }) => ({
          ...member,
          className: clsx(className, isSelf ? 'cursor-default!' : ''),
        })),
    },
  ];

  const handleViewAll = () => {
    if (isMobile) {
      navigate('/settings/members');
      closeSidebarMobileDrawer();
      return;
    }

    if (isSubmenuOverlay === 'members') {
      setIsSubmenuOverlay(null);
    } else {
      setIsSubmenuOverlay('members');
    }
  };

  if (initialLoading) {
    return (
      <div className="p-4 space-y-2">
        <Skeleton active paragraph={false} />
        <Skeleton active paragraph={false} />
      </div>
    );
  }

  if (!members?.length) {
    return (
      <div className={clsx('p-4', sidebarCollapsed ? 'px-0 pt-3' : 'bg-gray-100')}>
        <p className={clsx('text-text-600 text-sm text-center mb-4 uppercase', sidebarCollapsed ? 'block' : 'hidden')}>
          {t('member', { count: 5 })}
        </p>
        <Empty
          description={!sidebarCollapsed ? t('you_dont_have_any_members') : ''}
          classNames={{
            image: sidebarCollapsed ? 'h-10! mb-0! mx-auto' : '',
          }}
        />
      </div>
    );
  }

  return (
    <>
      <div className={clsx(sidebarCollapsed ? 'hidden' : '')}>
        <Menu
          selectedKeys={[String(memberId)]}
          className="custom-menu custom-sidebar-menu px-2.5! [&_.ant-menu-title-content]:order-1! [&_.ant-menu-submenu-title]:px-0.5!"
          inlineCollapsed={false}
          defaultOpenKeys={['members']}
          mode="inline"
          items={items}
        />
        {hasNextPage ? (
          <div
            id="view-all"
            className={clsx(
              'flex items-center ml-3 mt-1.5 gap-1 cursor-pointer w-fit py-1 px-2 rounded-full hover:text-primary-400',
              isSubmenuOverlay === 'members' ? 'text-primary-600 bg-primary-50' : 'text-text-600',
              hasNextPage ? '' : 'invisible'
            )}
            onClick={handleViewAll}
          >
            <p className="inline cursor-pointer text-xs tracking-wider rounded-xl uppercase!">{t('view_all')}</p>
            <FaChevronRight size={10} style={{ transform: isSubmenuOverlay === 'members' ? 'rotate(180deg)' : '' }} />
          </div>
        ) : null}
      </div>
      <InviteMemberModal inviteController={inviteController} />
      <div className={clsx(sidebarCollapsed ? '' : 'hidden')}>
        <MobileMembersView {...props} />
      </div>
    </>
  );
}

export const SidebarMembersView = memo(SidebarMembers);
